import type { User, Unsubscribe } from 'firebase/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { env } from '../../config/env';
import type { FirebaseServices } from '../../firebase/client';
import type { Role } from '../../types/domain';

interface AuthValue {
  user: User | null;
  role: Role;
  loading: boolean;
  configured: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshClaims: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

async function resolveRole(user: User | null): Promise<Role> {
  if (!user) return 'user';
  const token = await user.getIdTokenResult();
  if (token.claims.admin === true) return 'admin';
  if (token.claims.editor === true) return 'editor';
  return 'user';
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [services, setServices] = useState<FirebaseServices | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role>('user');
  const [loading, setLoading] = useState(env.firebaseConfigured);

  useEffect(() => {
    if (!env.firebaseConfigured) return;
    let disposed = false;
    let unsubscribe: Unsubscribe | undefined;
    void Promise.all([import('../../firebase/client'), import('firebase/auth')]).then(
      ([{ getFirebaseServices }, { onAuthStateChanged }]) => {
        if (disposed) return;
        const nextServices = getFirebaseServices();
        setServices(nextServices);
        if (!nextServices) {
          setLoading(false);
          return;
        }
        unsubscribe = onAuthStateChanged(nextServices.auth, (nextUser) => {
          setUser(nextUser);
          void resolveRole(nextUser)
            .then(setRole)
            .finally(() => setLoading(false));
        });
      },
    );
    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      if (!services) throw new Error('Firebase no está configurado.');
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      await signInWithEmailAndPassword(services.auth, email, password);
    },
    [services],
  );

  const register = useCallback(
    async (email: string, password: string, displayName: string) => {
      if (!services) throw new Error('Firebase no está configurado.');
      const {
        createUserWithEmailAndPassword,
        sendEmailVerification,
        updateProfile,
      } = await import('firebase/auth');
      const { doc, serverTimestamp, setDoc } = await import('firebase/firestore');
      const credential = await createUserWithEmailAndPassword(services.auth, email, password);
      await updateProfile(credential.user, { displayName });
      await setDoc(doc(services.db, 'users', credential.user.uid), {
        displayName,
        normalizedDisplayName: displayName.trim().toLocaleLowerCase('es-MX'),
        email,
        photoURL: null,
        role: 'user',
        explorerNumber: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        acceptedTermsAt: serverTimestamp(),
      });
      await sendEmailVerification(credential.user);
    },
    [services],
  );

  const loginWithGoogle = useCallback(async () => {
    if (!services) throw new Error('Firebase no está configurado.');
    const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth');
    const { doc, getDoc, serverTimestamp, setDoc } = await import('firebase/firestore');
    const credential = await signInWithPopup(services.auth, new GoogleAuthProvider());
    const ref = doc(services.db, 'users', credential.user.uid);
    if (!(await getDoc(ref)).exists()) {
      await setDoc(ref, {
        displayName: credential.user.displayName ?? 'Explorador',
        normalizedDisplayName: (credential.user.displayName ?? 'Explorador')
          .trim()
          .toLocaleLowerCase('es-MX'),
        email: credential.user.email ?? '',
        photoURL: credential.user.photoURL,
        role: 'user',
        explorerNumber: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        acceptedTermsAt: serverTimestamp(),
      });
    }
  }, [services]);

  const resetPassword = useCallback(
    async (email: string) => {
      if (!services) throw new Error('Firebase no está configurado.');
      const { sendPasswordResetEmail } = await import('firebase/auth');
      await sendPasswordResetEmail(services.auth, email);
    },
    [services],
  );

  const signOut = useCallback(async () => {
    if (!services) return;
    const { signOut } = await import('firebase/auth');
    await signOut(services.auth);
  }, [services]);

  const refreshClaims = useCallback(async () => {
    if (!user) return;
    await user.getIdToken(true);
    setRole(await resolveRole(user));
  }, [user]);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      role,
      loading,
      configured: env.firebaseConfigured,
      login,
      register,
      loginWithGoogle,
      resetPassword,
      signOut,
      refreshClaims,
    }),
    [
      user,
      role,
      loading,
      login,
      register,
      loginWithGoogle,
      resetPassword,
      signOut,
      refreshClaims,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth debe usarse dentro de AuthProvider.');
  return value;
}
